import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { formatApiErrorDetail } from "../lib/api";

export default function Register() {
  const { t } = useLang();
  const { register, user, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", surname: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/profile" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error(t("auth.password_short")); return; }
    setSubmitting(true);
    try {
      const u = await register(form);
      toast.success(t("auth.welcome") + ", " + u.name);
      nav("/profile");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Layout>
      <section className="fd-container py-20 md:py-32 flex justify-center" data-testid="register-page">
        <div className="w-full max-w-md">
          <p className="fd-label mb-4">— {t("auth.register")}</p>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight text-neutral-900 mb-10">{t("auth.register_title")}</h1>
          <form onSubmit={onSubmit} className="space-y-6" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="fd-label block mb-2">{t("auth.name")}</label>
                <input required value={form.name} onChange={set("name")}
                  className="w-full border border-neutral-300 focus:border-neutral-900 outline-none px-4 py-3 text-neutral-900 bg-white"
                  data-testid="register-name" />
              </div>
              <div>
                <label className="fd-label block mb-2">{t("auth.surname")}</label>
                <input required value={form.surname} onChange={set("surname")}
                  className="w-full border border-neutral-300 focus:border-neutral-900 outline-none px-4 py-3 text-neutral-900 bg-white"
                  data-testid="register-surname" />
              </div>
            </div>
            <div>
              <label className="fd-label block mb-2">{t("auth.email")}</label>
              <input type="email" required value={form.email} onChange={set("email")}
                className="w-full border border-neutral-300 focus:border-neutral-900 outline-none px-4 py-3 text-neutral-900 bg-white"
                data-testid="register-email" />
            </div>
            <div>
              <label className="fd-label block mb-2">{t("auth.password")}</label>
              <input type="password" required minLength={6} value={form.password} onChange={set("password")}
                className="w-full border border-neutral-300 focus:border-neutral-900 outline-none px-4 py-3 text-neutral-900 bg-white"
                data-testid="register-password" />
              <p className="text-xs text-neutral-400 mt-1">{t("auth.password_hint")}</p>
            </div>
            <button type="submit" disabled={submitting} className="fd-btn-primary w-full justify-center" data-testid="register-submit">
              {submitting ? t("common.loading") : t("auth.register")}
            </button>
            <p className="text-sm text-neutral-500 text-center">
              {t("auth.have_account")} <Link to="/login" className="text-neutral-900 underline" data-testid="link-login">{t("auth.login")}</Link>
            </p>
          </form>
        </div>
      </section>
    </Layout>
  );
}
